import type { FC, RefObject } from 'react'
import type { Item as Ga4ProductItem } from 'utils/tracking/hooks/use-products-impression-tracking'

import { SDS_COLOR_WHITE } from '@s-group/design-system-tokens/web/tokens/colors.es6'
import {
  SDS_FONT_SIZE_HEADING_MEDIUM,
  SDS_FONT_SIZE_HEADING_XSMALL,
  SDS_FONT_WEIGHT_REGULAR,
} from '@s-group/design-system-tokens/web/tokens/font.es6'
import { SDS_BRAND_COLOR_BORDER_WEAK_GREY } from '@s-group/design-system-tokens/web/tokens/s-kaupat/colors.es6'
import {
  SDS_SPACE_MEDIUM,
  SDS_SPACE_SMALL,
  SDS_SPACE_XLARGE,
  SDS_SPACE_XSMALL,
  SDS_SPACE_XXXSMALL,
} from '@s-group/design-system-tokens/web/tokens/space.es6'
import { minWidthFromTheme } from '@sok/design-system'
import { Carousel } from 'components/Carousel'
import { ProductListItem } from 'components/ProductListItem'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useComplementaryProductsContext } from 'services/ComplementaryProducts/complementary-products-provider'
import styled from 'styled-components'
import { useDeliveryDate } from 'utils/hooks/delivery/use-delivery-date'
import { useIsIntersecting } from 'utils/hooks/use-is-intersecting'
import { isNonNullable } from 'utils/nullable'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { mapGa4Availabilities } from 'utils/tracking/event-product-mapper'
import { TrackingContext } from 'utils/tracking/interfaces/data-layer-events'
import { GA4_trackImpressions } from 'utils/tracking/track-product-impressions'

const listName = 'complementary-products: cart'

const useImpressionTracking = (options: {
  items?: Ga4ProductItem[]
  ref?: RefObject<HTMLElement>
  impressionTracked: boolean
  markAsTracked: () => void
}) => {
  const { items, ref, impressionTracked, markAsTracked } = options
  const isIntersecting = useIsIntersecting(ref, { once: true })
  const availabilityDate = useDeliveryDate()
  useEffect(() => {
    if ((ref && !isIntersecting) || !items?.length || impressionTracked) return
    GA4_trackImpressions({
      products: items.filter(isNonNullable).map(mapGa4Availabilities(availabilityDate)),
      listName,
      trackingContext: TrackingContext.CHECKOUT_SUMMARY,
    })
    markAsTracked()
  }, [availabilityDate, isIntersecting, items, ref, impressionTracked, markAsTracked])
}

const _ComplementaryProductsMobile: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLElement>(null)
  const { complementaryProductsQuery, impressionTracked, markAsTracked } =
    useComplementaryProductsContext()
  const complementaryProducts = useMemo(
    () => complementaryProductsQuery?.data?.complementaryProducts || [],
    [complementaryProductsQuery],
  )
  useImpressionTracking({ items: complementaryProducts, ref, impressionTracked, markAsTracked })

  /**
   * Do not render in case of error, or no complementary products found.
   */
  if (
    complementaryProductsQuery?.error ||
    (!complementaryProductsQuery?.loading && !complementaryProducts.length)
  ) {
    return null
  }

  return (
    <TrackingContextProvider
      metadata={{ listName }}
      trackingContext={TrackingContext.CHECKOUT_SUMMARY}
    >
      <article className={className} data-test-id="complementary-products-mobile" ref={ref}>
        {complementaryProductsQuery?.loading ? (
          <LoadingIndicator>{t`Loading...`}</LoadingIndicator>
        ) : (
          <>
            <h2>{t`Do you need these?`}</h2>
            <TrackingContextProvider listName={listName}>
              <Carousel
                products={complementaryProducts}
                data-test-id="complementary-products-list-mobile"
              />
            </TrackingContextProvider>
          </>
        )}
      </article>
    </TrackingContextProvider>
  )
}

const _ComplementaryProductsDesktop: FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLElement>(null)
  const { complementaryProductsQuery, impressionTracked, markAsTracked } =
    useComplementaryProductsContext()
  const complementaryProducts = useMemo(
    () => complementaryProductsQuery?.data?.complementaryProducts || [],
    [complementaryProductsQuery],
  )
  useImpressionTracking({ items: complementaryProducts, ref, impressionTracked, markAsTracked })

  /**
   * Do not render in case of error, or no complementary products found.
   */
  if (
    complementaryProductsQuery?.error ||
    (!complementaryProductsQuery?.loading && !complementaryProducts.length)
  ) {
    return null
  }

  return (
    <TrackingContextProvider
      metadata={{ listName }}
      trackingContext={TrackingContext.CHECKOUT_SUMMARY}
    >
      <section className={className} data-test-id="complementary-products-desktop" ref={ref}>
        {complementaryProductsQuery?.loading ? (
          <LoadingIndicator>{t`Loading...`}</LoadingIndicator>
        ) : (
          <>
            <h3>
              <div>{t`Do you need these?`}</div>
            </h3>
            <div className="product-list" data-test-id="complementary-products-list-desktop">
              {complementaryProducts.map((product, i) => (
                <TrackingContextProvider
                  key={`${product.id}-${i}`}
                  listPosition={i}
                  listName={listName}
                >
                  <ProductListItem disabled={!!product.isGlobalFallback} product={product} />
                </TrackingContextProvider>
              ))}
            </div>
          </>
        )}
      </section>
    </TrackingContextProvider>
  )
}

export const ComplementaryProductsMobile = styled(_ComplementaryProductsMobile)(({ theme }) => ({
  marginTop: SDS_SPACE_XLARGE,
  h2: {
    fontSize: SDS_FONT_SIZE_HEADING_MEDIUM,
    fontWeight: SDS_FONT_WEIGHT_REGULAR,
    padding: `0 ${SDS_SPACE_SMALL}`,
  },
  [minWidthFromTheme(theme).desktop]: {
    display: 'none',
  },
}))

export const ComplementaryProductsDesktop = styled(_ComplementaryProductsDesktop)({
  height: '100%',
  overflow: 'auto',
  h3: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: `${SDS_SPACE_SMALL} ${SDS_SPACE_MEDIUM} 0`,
    backgroundColor: SDS_COLOR_WHITE,
    div: {
      borderBottom: `1px solid ${SDS_BRAND_COLOR_BORDER_WEAK_GREY}`,
      paddingBottom: SDS_SPACE_XSMALL,
      fontSize: SDS_FONT_SIZE_HEADING_XSMALL,
      fontWeight: SDS_FONT_WEIGHT_REGULAR,
    },
  },
  '.product-list': {
    marginTop: SDS_SPACE_XSMALL,
    padding: `0 ${SDS_SPACE_SMALL}`,
    display: 'flex',
    flexWrap: 'wrap',
    '> div': {
      flex: '0 0 50%',
      padding: SDS_SPACE_XXXSMALL,
    },
  },
})

const LoadingIndicator = styled.p({
  padding: SDS_SPACE_SMALL,
})
